import { mhecommLocalStorage } from './local-storage';
import { findClosest } from '../common/utils/util';

const MHECOMM_DATA_LAYER_STATE = 'MHECOMM_DATA_LAYER_STATE';
export const EXPIRATION_TIME_IN_MINS = 60 * 24;

declare global {
  interface Window {
    Mhecomm: any;
    dataLayer: any;
    adobeDataLayer: any;
    s: any;
    _satellite: any;
  }
}

/**
 * Emits an event from the provided element based on the passed in configurations.
 * @param config An object containing the configurable properties.
 * @param config.element The element to emit the event from.
 * @param config.name The name of the event. This will automatically be added to the 'eventName'
 *                    property of the event details if the details is an object and the eventName
 *                    property is not already present.
 * @param config.detail The object that will be emitted with the event.
 * @param config.dataLayer If true, the event will be automattically added to the google and adobe analytics data layer.
 * @param config.bubbles If true, the event will bubble up the DOM.
 * @param config.composed If true, the event will pass shadow dom borders.
 */
export default function emit({
  element,
  name,
  detail,
  state,
  dataLayer = true,
  bubbles = true,
  composed = true,
}: {
  element?: Element;
  name: string;
  detail?: any;
  state?: any;
  dataLayer?: boolean;
  bubbles?: boolean;
  composed?: boolean;
}) {
  if (detail) {
    if (!detail.comp) detail.comp = element?.tagName?.toLowerCase();

    if (!detail.eventName) detail.eventName = name;
  }

  if (dataLayer) {
    pushToDatalayer(name, detail, state, element);
  }

  if (element) {
    element.dispatchEvent(
      new CustomEvent(name, {
        detail: detail,
        bubbles: bubbles,
        composed: composed,
      })
    );
  }
}

/**
 * @param name The name of the event.
 * @param detail Any details about the event that need captured in analytics.
 * @param state Any updates to the static data layer.
 */
function pushToDatalayer(name: string, detail: any, state?: any, element?: Element) {
  const eventData: any = { event: name, eventInfo: detail, ...state };
  eventData.eventInfo = eventData.eventInfo || {};

  if (findClosest(element, 'header')) {
    eventData.eventInfo.area = 'header';
  } else if (findClosest(element, 'main')) {
    eventData.eventInfo.area = 'main';
  } else if (findClosest(element, 'footer')) {
    eventData.eventInfo.area = 'footer';
  } else if (findClosest(element, 'div.modal')) {
    eventData.eventInfo.area = findClosest(element, 'div.modal').getAttribute('aria-labelledby')?.includes('header')
      ? 'header'
      : '';
  }

  if (!eventData.eventInfo.id) {
    const closestWithId = findClosest(element, `[id]`);
    if (closestWithId) eventData.eventInfo.id = closestWithId.id;
  }

  const areaElement: any = findClosest(element, '[data-area-name]');
  if (areaElement?.dataset?.areaName) eventData.eventInfo.subarea = areaElement.dataset.areaName;

  // Logging both the object and the stringified version so that you can
  // see the data even for actions that navigate you away from the page.
  // This requires verbose logging and preserve log to both be turned on.
  console.debug(eventData);
  console.debug(JSON.stringify(eventData));

  if (typeof window.dataLayer === 'undefined') {
    window.dataLayer = [];
  }
  window.dataLayer.push(eventData);

  if (typeof window.adobeDataLayer === 'undefined') {
    window.adobeDataLayer = [];
  }
  window.adobeDataLayer.push(eventData);

  // Push data into the session data layer for debugging purposes
  mhecommLocalStorage.pushDataLayer(eventData);

  if (typeof window.adobeDataLayer.getState === 'function') {
    mhecommLocalStorage.set(MHECOMM_DATA_LAYER_STATE, window.adobeDataLayer.getState(), EXPIRATION_TIME_IN_MINS);
  }
}

window.Mhecomm = window.Mhecomm || {};
window.Mhecomm.emit = pushToDatalayer;
